import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useCompany } from "./useCompany";
import analytics from "@/lib/segment";

export interface MaintenanceTask {
  id: string;
  user_id: string;
  company_id?: string | null;
  vehicle_id: string;
  task_name: string;
  due_date?: string | null;
  due_at_mileage?: number | null;
  description?: string | null;
  attachments: string[];
  is_completed: boolean;
  completed_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface MaintenanceTaskFormData {
  vehicle_id: string;
  task_name: string;
  due_date?: string;
  due_at_mileage?: number;
  description?: string;
  attachments?: string[];
}

export const useMaintenance = () => {
  const queryClient = useQueryClient();
  const { company } = useCompany();

  const { data: tasks, isLoading } = useQuery({
    queryKey: ["maintenance-tasks", company?.id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [] as MaintenanceTask[];

      let query = supabase
        .from("maintenance_tasks")
        .select("*")
        .order("due_date", { ascending: true, nullsFirst: false });

      if (company?.id) {
        query = query.eq("company_id", company.id);
      } else {
        query = query.eq("user_id", user.id);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as MaintenanceTask[];
    },
  });

  const addTask = useMutation({
    mutationFn: async (taskData: MaintenanceTaskFormData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("maintenance_tasks")
        .insert({
          ...taskData,
          user_id: user.id,
          company_id: company?.id ?? null,
          attachments: taskData.attachments || [],
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["maintenance-tasks"] });
      analytics.track("Maintenance Task Added", { task_name: variables.task_name });
      toast.success("Maintenance task added");
    },
    onError: (error) => {
      toast.error("Failed to add task: " + error.message);
    },
  });

  const updateTask = useMutation({
    mutationFn: async ({ id, ...taskData }: Partial<MaintenanceTaskFormData> & { id: string }) => {
      const { data, error } = await supabase
        .from("maintenance_tasks")
        .update(taskData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenance-tasks"] });
      toast.success("Maintenance task updated");
    },
    onError: (error) => {
      toast.error("Failed to update task: " + error.message);
    },
  });

  const toggleComplete = useMutation({
    mutationFn: async ({ id, is_completed }: { id: string; is_completed: boolean }) => {
      const { data, error } = await supabase
        .from("maintenance_tasks")
        .update({ 
          is_completed, 
          completed_at: is_completed ? new Date().toISOString() : null 
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["maintenance-tasks"] });
      if (variables.is_completed) {
        analytics.track("Maintenance Task Completed", { task_id: variables.id });
      }
    },
    onError: (error) => {
      toast.error("Failed to update task: " + error.message);
    },
  });

  const deleteTask = useMutation({
    mutationFn: async (taskId: string) => {
      const { error } = await supabase
        .from("maintenance_tasks")
        .delete()
        .eq("id", taskId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenance-tasks"] });
      toast.success("Maintenance task deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete task: " + error.message);
    },
  });

  const uploadAttachment = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${crypto.randomUUID()}.${fileExt}`;
    const filePath = `attachments/${fileName}`;

    const { error } = await supabase.storage
      .from("maintenance-attachments")
      .upload(filePath, file);

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from("maintenance-attachments")
      .getPublicUrl(filePath);

    return publicUrl;
  };

  return {
    tasks,
    isLoading,
    addTask,
    updateTask,
    toggleComplete,
    deleteTask,
    uploadAttachment,
  };
};
